/**
 * Show Timeline Builder
 *
 * Converts a ShowProject (with populated story.scenes[]) into a flat,
 * ordered array of ShowTimelineItem — the master timeline for the show.
 *
 * Timeline structure:
 *   [intro] → [story-1: story-counter → scene → scene → …] → [transition]
 *           → [story-2: story-counter → scene → …] → … → [outro]
 *
 * All times are in SECONDS from show start (t=0).
 *
 * This function is PURE — no side effects, easy to test.
 */

import type {
  ShowProject,
  ShowTimelineItem,
  ShowTimelineItemType,
  Story,
  Scene,
  SceneProps,
  ShowTemplate,
} from '../types';

const uuidv4 = () => crypto.randomUUID();

// ─── Duration constants ────────────────────────────────────────

const INTRO_DURATION_SEC = 5;      // show open animation
const OUTRO_DURATION_SEC = 4;      // end card
const STORY_COUNTER_SEC = 2.5;     // "खबर नंबर 3" standalone card
const TRANSITION_SEC = 1.0;        // inter-story cross-fade

// ─── Item factory helpers ──────────────────────────────────────

function makeItem(
  type: ShowTimelineItemType,
  startSec: number,
  durationSec: number,
  extra: Partial<ShowTimelineItem> = {},
): ShowTimelineItem {
  return {
    id: uuidv4(),
    type,
    startSec,
    endSec: startSec + durationSec,
    ...extra,
  };
}

// ─── Story counter scene props ────────────────────────────────

/**
 * Build a minimal SceneProps for a story-counter card.
 * Uses L5 (full screen headline) with the counter text as headline.
 */
function buildCounterSceneProps(
  story: Story,
  showTemplate: ShowTemplate,
  counterText: string,
): SceneProps {
  return {
    layoutId: 'L5',
    headline: counterText,
    assets: {},
    colorPalette: showTemplate.colorPalette,
    headlineFont: {
      ...showTemplate.headlineFont,
      fontSize: 72,
      letterSpacing: 2,
    },
    subheadlineFont: showTemplate.subheadlineFont,
    showTicker: false,
    showLogo: true,
    showTopBar: false,
    showStoryCounter: false,
    isBreaking: story.isBreaking,
    breakingLabel: story.isBreaking ? 'BREAKING NEWS' : undefined,
  };
}

// ─── Build timeline for one story ─────────────────────────────

function buildStoryItems(
  story: Story,
  showTemplate: ShowTemplate,
  cursor: number,
): { items: ShowTimelineItem[]; nextCursor: number } {
  const items: ShowTimelineItem[] = [];

  // 1. Story counter card (if template uses counters)
  if (showTemplate.globalOverlay.storyCounter && story.scenes.length > 0) {
    const counterText = story.scenes[0]?.props.storyCounterText ?? `Story ${story.index}`;
    items.push(makeItem('story-counter', cursor, STORY_COUNTER_SEC, {
      storyId: story.id,
      storyIndex: story.index,
      sceneProps: buildCounterSceneProps(story, showTemplate, counterText),
    }));
    cursor += STORY_COUNTER_SEC;
  }

  // 2. Scene items
  for (let i = 0; i < story.scenes.length; i++) {
    const scene: Scene = story.scenes[i];

    items.push(makeItem('scene', cursor, scene.duration, {
      storyId: story.id,
      storyIndex: story.index,
      sceneId: scene.id,
      sceneProps: scene.props,
    }));

    cursor += scene.duration;

    // Add intra-story transition between scenes (not after the last)
    if (i < story.scenes.length - 1) {
      const transitionDur = showTemplate.rules.shortTransitionDuration;
      items.push(makeItem('transition', cursor, transitionDur, {
        storyId: story.id,
      }));
      cursor += transitionDur;
    }
  }

  return { items, nextCursor: cursor };
}

// ─── Main export ───────────────────────────────────────────────

export interface TimelineBuilderResult {
  timeline: ShowTimelineItem[];
  /** Total show duration in seconds */
  totalDurationSec: number;
  /** Total duration in frames at the show's fps */
  totalDurationFrames: number;
}

/**
 * Build the complete master timeline for a show.
 *
 * @param project  ShowProject with story.scenes[] already populated
 *                 (run generateAllScenes() first)
 * @returns        Flat, time-ordered ShowTimelineItem[] + total duration
 */
export function buildShowTimeline(project: ShowProject): TimelineBuilderResult {
  const { showTemplate, stories, outputSettings } = project;
  const fps = outputSettings?.fps ?? 30;
  const items: ShowTimelineItem[] = [];
  let cursor = 0;

  // ── Intro ──────────────────────────────────────────────────
  items.push(makeItem('intro', cursor, INTRO_DURATION_SEC));
  cursor += INTRO_DURATION_SEC;

  // ── Stories ────────────────────────────────────────────────
  const enabledStories = stories.filter((s) => s.enabled);

  for (let i = 0; i < enabledStories.length; i++) {
    const story = enabledStories[i];

    // Inter-story transition (not before the first story)
    if (i > 0) {
      items.push(makeItem('transition', cursor, TRANSITION_SEC, {
        storyId: story.id,
      }));
      cursor += TRANSITION_SEC;
    }

    const { items: storyItems, nextCursor } = buildStoryItems(
      story,
      showTemplate,
      cursor,
    );
    items.push(...storyItems);
    cursor = nextCursor;
  }

  // ── Outro ──────────────────────────────────────────────────
  items.push(makeItem('outro', cursor, OUTRO_DURATION_SEC));
  cursor += OUTRO_DURATION_SEC;

  return {
    timeline: items,
    totalDurationSec: cursor,
    totalDurationFrames: Math.round(cursor * fps),
  };
}

// ─── Utility: get all scene items for a story ─────────────────

export function getStorySceneItems(
  timeline: ShowTimelineItem[],
  storyId: string,
): ShowTimelineItem[] {
  return timeline.filter((item) => item.type === 'scene' && item.storyId === storyId);
}

// ─── Utility: get total story duration from timeline ──────────

export function getStoryDuration(
  timeline: ShowTimelineItem[],
  storyId: string,
): number {
  const items = timeline.filter((item) => item.storyId === storyId);
  if (items.length === 0) return 0;
  const start = items[0].startSec;
  const end = items[items.length - 1].endSec;
  return end - start;
}

// ─── Utility: timeline summary (for debugging / UI) ───────────

export interface TimelineSummary {
  totalDurationSec: number;
  storyCount: number;
  sceneCount: number;
  introDuration: number;
  outroDuration: number;
  stories: Array<{
    storyId: string;
    storyIndex: number;
    startSec: number;
    endSec: number;
    sceneCount: number;
  }>;
}

export function summarizeTimeline(
  result: TimelineBuilderResult,
  stories: Story[],
): TimelineSummary {
  const { timeline, totalDurationSec } = result;
  const sceneItems = timeline.filter((i) => i.type === 'scene');
  const introDur = timeline.find((i) => i.type === 'intro')?.endSec ?? INTRO_DURATION_SEC;
  const outroDur = OUTRO_DURATION_SEC;

  const storyGroups = stories
    .filter((s) => s.enabled)
    .map((story) => {
      const storyItems = timeline.filter((i) => i.storyId === story.id);
      if (storyItems.length === 0) return null;
      return {
        storyId: story.id,
        storyIndex: story.index,
        startSec: storyItems[0].startSec,
        endSec: storyItems[storyItems.length - 1].endSec,
        sceneCount: storyItems.filter((i) => i.type === 'scene').length,
      };
    })
    .filter(Boolean) as TimelineSummary['stories'];

  return {
    totalDurationSec,
    storyCount: storyGroups.length,
    sceneCount: sceneItems.length,
    introDuration: introDur,
    outroDuration: outroDur,
    stories: storyGroups,
  };
}
